import apiInstance, { cachedApiInstance } from "api";

/**
 * Time Series API Service
 *
 * This service provides methods to query time-series data from QuestDB
 * through the centralized API layer. It replaces direct QuestDB connections
 * with proper validation, caching, and error handling.
 */
const TimeSeriesService = {
  /**
   * Get the latest data point for a device
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type (e.g., 'CP01', 'EN01')
   * @param {string} params.timeKey - Time column key (e.g., 'tTimesent', 'tTimerecieved')
   * @param {string[]} params.dataSources - Array of data columns to retrieve
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getLatest: async (deviceNumber, params, signal) => {
    const queryParams = new URLSearchParams();
    queryParams.set("deviceType", params.deviceType);
    queryParams.set("timeKey", params.timeKey);
    params.dataSources.forEach((ds) => queryParams.append("dataSources", ds));

    return apiInstance.get(`/time-series/${deviceNumber}/latest?${queryParams.toString()}`, {
      signal,
    });
  },

  /**
   * Get chart data with aggregation for a device
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type
   * @param {string} params.timeKey - Time column key
   * @param {string[]} params.dataSources - Array of data columns to retrieve
   * @param {string} params.timeInterval - Time interval (e.g., '1h', '24h', '7d', 'custom')
   * @param {string} params.sampleBy - Aggregation interval (e.g., '1m', '5m', '1h')
   * @param {string} [params.startDate] - Start date for custom interval (ISO format)
   * @param {string} [params.endDate] - End date for custom interval (ISO format)
   * @param {number} [params.channelId] - Optional channel ID filter
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getChart: async (deviceNumber, params, signal) => {
    const queryParams = new URLSearchParams();
    queryParams.set("deviceType", params.deviceType);
    queryParams.set("timeKey", params.timeKey);
    queryParams.set("timeInterval", params.timeInterval);
    queryParams.set("sampleBy", params.sampleBy || "1m");
    params.dataSources.forEach((ds) => queryParams.append("dataSources", ds));

    if (params.startDate) queryParams.set("startDate", params.startDate);
    if (params.endDate) queryParams.set("endDate", params.endDate);
    if (params.channelId) queryParams.set("channelId", params.channelId);

    // Use cached API for historical queries
    const api = isHistoricalQuery(params.timeInterval) ? cachedApiInstance : apiInstance;

    return api.get(`/time-series/${deviceNumber}/chart?${queryParams.toString()}`, {
      signal,
    });
  },

  /**
   * Get paginated grid data for a device
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type
   * @param {string} params.timeKey - Time column key
   * @param {string[]} params.dataSources - Array of data columns to retrieve
   * @param {string} params.timeInterval - Time interval
   * @param {number} [params.page=1] - Page number (1-indexed)
   * @param {number} [params.pageSize=20] - Rows per page
   * @param {boolean} [params.includeCount=true] - Include total count for pagination
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getGrid: async (deviceNumber, params, signal) => {
    const queryParams = new URLSearchParams();
    queryParams.set("deviceType", params.deviceType);
    queryParams.set("timeKey", params.timeKey);
    queryParams.set("timeInterval", params.timeInterval);
    queryParams.set("page", (params.page || 1).toString());
    queryParams.set("pageSize", (params.pageSize || 20).toString());
    queryParams.set("includeCount", (params.includeCount !== false).toString());
    params.dataSources.forEach((ds) => queryParams.append("dataSources", ds));

    if (params.startDate) queryParams.set("startDate", params.startDate);
    if (params.endDate) queryParams.set("endDate", params.endDate);

    return apiInstance.get(`/time-series/${deviceNumber}/grid?${queryParams.toString()}`, {
      signal,
    });
  },

  /**
   * Get latest data for multiple devices at once
   * @param {Object} params - Query parameters
   * @param {string[]} params.deviceNumbers - Array of device identifiers
   * @param {string} params.deviceType - Device type (all devices must be same type)
   * @param {string} params.timeKey - Time column key
   * @param {string[]} params.dataSources - Array of data columns to retrieve
   * @param {string} [params.orderBy] - Column to order by
   * @param {string} [params.orderDirection='DESC'] - Order direction ('ASC' or 'DESC')
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getMultiDeviceLatest: async (params, signal) => {
    return apiInstance.post("/time-series/multi-device/latest", params, { signal });
  },

  /**
   * Get statistics (min, max, avg) for a device
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type
   * @param {string} params.timeKey - Time column key
   * @param {string[]} params.dataSources - Array of data columns to aggregate
   * @param {string} params.timeInterval - Time interval
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getStats: async (deviceNumber, params, signal) => {
    const queryParams = new URLSearchParams();
    queryParams.set("deviceType", params.deviceType);
    queryParams.set("timeKey", params.timeKey);
    queryParams.set("timeInterval", params.timeInterval);
    params.dataSources.forEach((ds) => queryParams.append("dataSources", ds));

    if (params.startDate) queryParams.set("startDate", params.startDate);
    if (params.endDate) queryParams.set("endDate", params.endDate);

    // Always use cached API for stats (expensive queries)
    return cachedApiInstance.get(`/time-series/${deviceNumber}/stats?${queryParams.toString()}`, {
      signal,
    });
  },

  /**
   * Get count of records matching specific conditions
   * Used for calculating percentages like "compressor on time" or "dryer efficiency"
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type
   * @param {string} params.timeKey - Time column key
   * @param {string} params.timeInterval - Time interval
   * @param {Array<Object>} params.conditions - Array of condition objects
   * @param {string} params.conditions[].column - Column name to compare
   * @param {string} params.conditions[].operator - Comparison operator (=, !=, >, <, >=, <=)
   * @param {number} params.conditions[].value - Numeric value to compare against
   * @param {string} [params.conditions[].logicalOperator='OR'] - Logical operator for next condition (AND, OR)
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getConditionalCount: async (deviceNumber, params, signal) => {
    // Use cached API for conditional count queries (expensive)
    return cachedApiInstance.post(`/time-series/${deviceNumber}/conditional-count`, params, {
      signal,
    });
  },

  /**
   * Get aggregated stats (AVG) for records matching specific conditions
   * Used for calculating dewpoint efficiency and similar metrics
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type
   * @param {string} params.timeKey - Time column key
   * @param {string[]} params.dataSources - Array of data columns to aggregate (AVG)
   * @param {string} params.timeInterval - Time interval
   * @param {Array<Object>} params.conditions - Array of condition objects
   * @param {string} params.conditions[].column - Column name to compare
   * @param {string} params.conditions[].operator - Comparison operator (=, !=, >, <, >=, <=)
   * @param {number} params.conditions[].value - Numeric value to compare against
   * @param {string} [params.conditions[].logicalOperator='OR'] - Logical operator for next condition
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getConditionalStats: async (deviceNumber, params, signal) => {
    // Use cached API for conditional stats queries (expensive)
    return cachedApiInstance.post(`/time-series/${deviceNumber}/conditional-stats`, params, {
      signal,
    });
  },

  /**
   * Get range stats (MAX - MIN) for data sources, useful for calculating totals
   * Used for calculating energy usage, runtime totals, etc.
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type
   * @param {string} params.timeKey - Time column key
   * @param {string[]} params.dataSources - Array of data columns to calculate range for
   * @param {string} params.timeInterval - Time interval
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {number} [params.channelId] - Optional channel ID filter (for Energy_Master queries)
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getRangeStats: async (deviceNumber, params, signal) => {
    // Use cached API for range stats queries (expensive)
    return cachedApiInstance.post(`/time-series/${deviceNumber}/range-stats`, params, {
      signal,
    });
  },

  /**
   * Get range stats (MAX - MIN) for multiple devices, useful for top consumers dashboards
   * @param {Object} params - Query parameters
   * @param {string[]} params.deviceNumbers - Array of device identifiers
   * @param {string} params.deviceType - Device type (e.g., 'Energy_Master')
   * @param {string} params.timeKey - Time column key (e.g., 'tTimerecieved')
   * @param {string[]} params.dataSources - Array of data columns to calculate range for
   * @param {string} params.timeInterval - Time interval (e.g., '24h', '7d', 'custom')
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {string} [params.orderByColumn] - Column to order results by (must be one of dataSources)
   * @param {string} [params.orderDirection='DESC'] - Order direction ('ASC' or 'DESC')
   * @param {boolean} [params.groupByChannel=true] - Whether to group by channel
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getMultiDeviceRangeStats: async (params, signal) => {
    // Use cached API for multi-device range stats queries (expensive)
    return cachedApiInstance.post("/time-series/multi-device/range-stats", params, {
      signal,
    });
  },

  /**
   * Get restart events using LAG window function to detect when iCount increases
   * Used for displaying compressor restart timeline charts
   * @param {string} deviceNumber - The device identifier
   * @param {Object} params - Query parameters
   * @param {string} params.deviceType - Device type (e.g., 'Energy_Master')
   * @param {string} params.timeKey - Time column key (e.g., 'tTimerecieved')
   * @param {string} params.timeInterval - Time interval (e.g., '24h', '7d', 'custom')
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {number} [params.channelId] - Optional channel ID filter
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getRestartEvents: async (deviceNumber, params, signal) => {
    // Use cached API for restart events queries
    return cachedApiInstance.post(`/time-series/${deviceNumber}/restart-events`, params, {
      signal,
    });
  },

  /**
   * Get monthly aggregation of power consumption for multiple devices
   * Used for monthly bar charts in energy dashboards
   * @param {Object} params - Query parameters
   * @param {string[]} params.deviceNumbers - Array of device identifiers
   * @param {string} params.deviceType - Device type (e.g., 'Energy_Master')
   * @param {string} params.timeKey - Time column key (e.g., 'tTimerecieved')
   * @param {string[]} params.dataSources - Array of data columns to aggregate (e.g., ['rKwHr'])
   * @param {string} params.timeInterval - Time interval (e.g., 'custom')
   * @param {string} [params.startDate] - Start date for custom interval
   * @param {string} [params.endDate] - End date for custom interval
   * @param {AbortSignal} [signal] - Optional abort signal for cancellation
   */
  getMonthlyAggregation: async (params, signal) => {
    // Use cached API for monthly aggregation queries (expensive)
    return cachedApiInstance.post("/time-series/multi-device/monthly-aggregation", params, {
      signal,
    });
  },
};

/**
 * Helper to determine if a query is historical (for caching)
 */
function isHistoricalQuery(timeInterval) {
  const historicalIntervals = ["24h", "7d", "30d", "90d", "180d", "365d", "custom"];
  return historicalIntervals.includes(timeInterval?.toLowerCase());
}

/**
 * Device type configurations
 * Maps device types to their available data sources and time keys
 */
export const DeviceTypeConfig = {
  CP01: {
    timeKey: "tTimerecieved",
    dataSources: [
      "rTemp",
      "rHumid",
      "rCurrent",
      "rTemp1",
      "rTemp2",
      "rPressure",
      "rKws",
      "xGreenLED",
      "xYellowLED",
      "xRedLED",
      "xBuzzer",
    ],
  },
  EN01: {
    timeKey: "tTimesent",
    dataSources: ["rVoltsLN", "rTemp", "rAmps", "rKwHr", "rHumid", "rVoltsLL"],
  },
  EN02: {
    timeKey: "tTime",
    dataSources: ["rVolts", "rAmps", "rKwHr"],
  },
  TA01: {
    timeKey: "tTimesent",
    dataSources: [
      "rPressure1",
      "rPressure2",
      "rTemp2",
      "rAmps",
      "rTemp",
      "rKwHr",
      "rHumid",
      "rVoltsLN",
      "rVoltsLL",
      "rTemp1",
    ],
  },
  DL01: {
    timeKey: "tTimesent",
    dataSources: ["y1", "y2", "y3", "y4", "SP", "PV", "PIDOUT"],
  },
  DL02: {
    timeKey: "tTimesent",
    dataSources: ["rTemp", "rPressure", "rDewpoint", "rFlow", "rAmps"],
  },
  DL03: {
    timeKey: "tTimesent",
    dataSources: [
      "rTemp",
      "rHumid",
      "rDewpoint",
      "rDewpointATM",
      "rPPM",
      "rTemperature",
      "rPressure",
      "SP",
      "PV",
      "PIDOUT",
    ],
  },
  VL01: {
    timeKey: "tTimesent",
    dataSources: ["rTemp", "rHumid", "rLevel", "rVolume"],
  },
  TL01: {
    timeKey: "tTimesent",
    dataSources: ["rTemp", "rHumid", "rLevel", "rVolume", "rDistance"],
  },
  Device_Heartbeat: {
    timeKey: "tTimerecieved",
    dataSources: ["RSSI", "sDevice", "sStyle"],
  },
  Energy_Master: {
    timeKey: "tTimerecieved",
    dataSources: ["sDevice", "sChannel", "rKwHr", "iCount", "iRuntime"],
  },
  Compressor_Restarts: {
    timeKey: "tTimerecieved",
    dataSources: ["sDevice", "restarts", "restartType"],
  },
  CCTL01: {
    timeKey: "tTimerecieved",
    dataSources: [
      "humidity",
      "temperature",
      "pressure",
      "relativeHumidity",
      "comp1Runtime",
      "comp2Runtime",
      "comp1Loadedtime",
      "comp2Loadedtime",
    ],
  },
};

/**
 * Sample intervals available for chart queries
 */
export const SampleIntervals = [
  { value: "1s", label: "1 Second" },
  { value: "5s", label: "5 Seconds" },
  { value: "10s", label: "10 Seconds" },
  { value: "30s", label: "30 Seconds" },
  { value: "1m", label: "1 Minute" },
  { value: "5m", label: "5 Minutes" },
  { value: "15m", label: "15 Minutes" },
  { value: "30m", label: "30 Minutes" },
  { value: "1h", label: "1 Hour" },
  { value: "6h", label: "6 Hours" },
  { value: "12h", label: "12 Hours" },
  { value: "24h", label: "24 Hours" },
];

/**
 * Time intervals available for filtering
 */
export const TimeIntervals = [
  { value: "1h", label: "Last Hour" },
  { value: "6h", label: "Last 6 Hours" },
  { value: "12h", label: "Last 12 Hours" },
  { value: "24h", label: "Last 24 Hours" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "custom", label: "Custom Range" },
];

export default TimeSeriesService;
